import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import reviewService from '../../services/reviewService';
import type { Review, ReviewStats, CreateReviewInput } from '../../services/reviewService';

interface ReviewState {
  reviews: Review[];
  lawyerStats: { [lawyerId: number]: ReviewStats };
  organizationStats: ReviewStats | null;
  loading: boolean;
  error: string | null;
}

const initialState: ReviewState = {
  reviews: [],
  lawyerStats: {},
  organizationStats: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchReviews = createAsyncThunk(
  'reviews/fetchReviews',
  async ({ lawyerId, clientId, caseId }: { lawyerId?: number; clientId?: number; caseId?: number }) => {
    const response = await reviewService.getReviews(lawyerId, clientId, caseId);
    return response.data;
  }
);

export const fetchLawyerStats = createAsyncThunk(
  'reviews/fetchLawyerStats',
  async (lawyerId: number) => {
    const response = await reviewService.getLawyerStats(lawyerId);
    return { lawyerId, stats: response.data };
  }
);

export const fetchOrganizationStats = createAsyncThunk(
  'reviews/fetchOrganizationStats',
  async (orgId: number) => {
    const response = await reviewService.getOrganizationStats(orgId);
    return response.data;
  }
);

export const createReview = createAsyncThunk(
  'reviews/createReview',
  async (data: CreateReviewInput) => {
    const response = await reviewService.createReview(data);
    return response.data;
  }
);

export const updateReview = createAsyncThunk(
  'reviews/updateReview',
  async ({ id, data }: { id: number; data: Partial<CreateReviewInput> }) => {
    const response = await reviewService.updateReview(id, data);
    return response.data;
  }
);

export const deleteReview = createAsyncThunk(
  'reviews/deleteReview',
  async (id: number) => {
    await reviewService.deleteReview(id);
    return id;
  }
);

export const verifyReview = createAsyncThunk(
  'reviews/verifyReview',
  async (id: number) => {
    const response = await reviewService.verifyReview(id);
    return response.data;
  }
);

export const searchReviews = createAsyncThunk(
  'reviews/searchReviews',
  async ({ query, filters }: { query: string; filters?: any }) => {
    const response = await reviewService.searchReviews(query, filters);
    return response.data;
  }
);

const reviewSlice = createSlice({
  name: 'reviews',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearReviews: (state) => {
      state.reviews = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch reviews
      .addCase(fetchReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReviews.fulfilled, (state, action) => {
        state.loading = false;
        state.reviews = action.payload;
      })
      .addCase(fetchReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch reviews';
      })
      
      // Fetch lawyer stats
      .addCase(fetchLawyerStats.fulfilled, (state, action) => {
        const { lawyerId, stats } = action.payload;
        state.lawyerStats[lawyerId] = stats;
      })
      .addCase(fetchLawyerStats.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch lawyer stats';
      })
      
      // Fetch organization stats
      .addCase(fetchOrganizationStats.fulfilled, (state, action) => {
        state.organizationStats = action.payload;
      })
      .addCase(fetchOrganizationStats.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch organization stats';
      })
      
      // Create review
      .addCase(createReview.pending, (state) => {
        state.loading = true;
      })
      .addCase(createReview.fulfilled, (state, action) => {
        state.loading = false;
        state.reviews.unshift(action.payload);
      })
      .addCase(createReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create review';
      })
      
      // Update review
      .addCase(updateReview.fulfilled, (state, action) => {
        const index = state.reviews.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.reviews[index] = action.payload;
        }
      })
      .addCase(updateReview.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update review';
      })
      
      // Delete review
      .addCase(deleteReview.fulfilled, (state, action) => {
        state.reviews = state.reviews.filter(r => r.id !== action.payload);
      })
      .addCase(deleteReview.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete review';
      })
      
      // Verify review
      .addCase(verifyReview.fulfilled, (state, action) => {
        const index = state.reviews.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.reviews[index] = action.payload;
        }
      })
      .addCase(verifyReview.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to verify review';
      })
      
      // Search reviews
      .addCase(searchReviews.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(searchReviews.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to search reviews';
      });
  },
});

export const { clearError, clearReviews } = reviewSlice.actions;
export default reviewSlice.reducer;
